import React, { useEffect, useState } from 'react';
import { Button, Checkbox, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { Share } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { useToast } from '../contexts/ToastContext';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { InlineMessage, LoadingState, SectionCard, StatusChip } from './ui';

export type SellThroughSharingMode = 'off' | 'all_vendors' | 'selected';

export interface SellThroughSharingSetting {
  grant: { mode: SellThroughSharingMode; vendorOrganizationIds: string[] };
  staleVendorOrganizationIds?: string[];
  eligibleVendors: Array<{
    vendorOrganizationId: string;
    vendorName: string;
    brands: Array<{ brandId: string; brandName: string }>;
  }>;
}

export const SELL_THROUGH_SHARING_QUERY_KEY = ['store-sell-through-sharing'] as const;

const ENDPOINT = '/store/settings/sell-through-sharing';

async function readBody(response: Response, fallback: string) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body?.success) {
    throw new Error(body?.error?.message || body?.message || fallback);
  }
  return body.data as SellThroughSharingSetting;
}

/**
 * Jordan decides whether vendors can see this store's sell-through (flag
 * `sell_through_sharing`): every vendor, or a checklist of the vendors of the
 * brands the store is verified for. Off until the store says otherwise.
 */
export const SellThroughSharingCard: React.FC = () => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { enabled } = useOrgFeatureFlag('sell_through_sharing');
  const [allVendors, setAllVendors] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  const { data, isLoading, isError } = useQuery({
    queryKey: SELL_THROUGH_SHARING_QUERY_KEY,
    queryFn: async () =>
      readBody(await makeApiCall(ENDPOINT, { headers: await getAuthHeaders() }), t('store.sellThroughSharing.loadError')),
    enabled,
  });

  useEffect(() => {
    if (!data) return;
    setAllVendors(data.grant.mode === 'all_vendors');
    const eligibleIds = new Set(data.eligibleVendors.map((v) => v.vendorOrganizationId));
    setSelected(
      data.grant.mode === 'selected' ? data.grant.vendorOrganizationIds.filter((id) => eligibleIds.has(id)) : [],
    );
  }, [data]);

  const save = useMutation({
    mutationFn: async () => {
      const eligibleIds = new Set((data?.eligibleVendors ?? []).map((v) => v.vendorOrganizationId));
      const chosen = selected.filter((id) => eligibleIds.has(id));
      const mode: SellThroughSharingMode = allVendors ? 'all_vendors' : chosen.length > 0 ? 'selected' : 'off';
      return readBody(
        await makeApiCall(ENDPOINT, {
          method: 'PUT',
          headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode, ...(mode === 'selected' ? { vendorOrganizationIds: chosen } : {}) }),
        }),
        t('store.sellThroughSharing.saveError'),
      );
    },
    onSuccess: () => {
      showToast(t('store.sellThroughSharing.saved'), 'success');
      queryClient.invalidateQueries({ queryKey: SELL_THROUGH_SHARING_QUERY_KEY });
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  if (!enabled) return null;

  const mode = data?.grant.mode ?? 'off';
  const chip =
    mode === 'all_vendors'
      ? { label: t('store.sellThroughSharing.chipAll'), tone: 'positive' as const }
      : mode === 'selected'
        ? {
            label: t('store.sellThroughSharing.chipSelected', { count: data?.grant.vendorOrganizationIds.length ?? 0 }),
            tone: 'positive' as const,
          }
        : { label: t('store.sellThroughSharing.chipOff'), tone: 'neutral' as const };

  const toggleVendor = (id: string) =>
    setSelected((current) => (current.includes(id) ? current.filter((v) => v !== id) : [...current, id]));

  return (
    <SectionCard title={t('store.sellThroughSharing.title')} leading={<Share color="primary" />}>
      {isLoading ? (
        <LoadingState />
      ) : isError || !data ? (
        <InlineMessage severity="error">{t('store.sellThroughSharing.loadError')}</InlineMessage>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('store.sellThroughSharing.intro')}
          </Typography>
          <Typography variant="body2">{t('store.sellThroughSharing.whatShared')}</Typography>
          {(data.staleVendorOrganizationIds?.length ?? 0) > 0 && (
            <InlineMessage severity="warning">{t('store.sellThroughSharing.staleDropped')}</InlineMessage>
          )}
          <div>
            <StatusChip label={chip.label} tone={chip.tone} />
          </div>
          <FormControlLabel
            control={<Switch checked={allVendors} onChange={(event) => setAllVendors(event.target.checked)} />}
            label={
              <Stack spacing={0.25}>
                <Typography variant="body2" fontWeight={600}>
                  {t('store.sellThroughSharing.allVendors')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('store.sellThroughSharing.allVendorsHelp')}
                </Typography>
              </Stack>
            }
          />
          {!allVendors && (
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                {data.eligibleVendors.length > 0
                  ? t('store.sellThroughSharing.selectedHelp')
                  : t('store.sellThroughSharing.noVendors')}
              </Typography>
              {data.eligibleVendors.map((vendor) => (
                <FormControlLabel
                  key={vendor.vendorOrganizationId}
                  control={
                    <Checkbox
                      checked={selected.includes(vendor.vendorOrganizationId)}
                      onChange={() => toggleVendor(vendor.vendorOrganizationId)}
                    />
                  }
                  label={`${vendor.vendorName} (${vendor.brands.map((b) => b.brandName).join(', ')})`}
                />
              ))}
            </Stack>
          )}
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" disabled={save.isPending} onClick={() => save.mutate()}>
              {t('store.sellThroughSharing.save')}
            </Button>
          </Stack>
        </Stack>
      )}
    </SectionCard>
  );
};

export default SellThroughSharingCard;
